import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { App, Popconfirm } from 'antd';
import { useNavigate } from 'react-router-dom';
import { listAdminAccounts } from '../../api/adminAccounts';
import { rejectAdminUser, unlinkGoogleFromUser } from '../../api/adminUsers';
import { getApiErrorMessage } from '../../utils/apiError';
import TableCard from '../../components/TableCard';

const statusLabel = (v) => ({ approved: 'Approuvé', pending: 'En attente', rejected: 'Rejeté' }[v] || v || '-');
const statusColor = (v) => ({ approved: 'green', pending: 'orange', rejected: 'red' }[v] || '#888');

export default function AdminAccounts() {
  const { message, notification } = App.useApp();
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const data = await listAdminAccounts();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) { notification.error({ message: getApiErrorMessage(e, 'Impossible de charger les comptes admin.'), placement: 'topRight' }); }
    finally { setLoading(false); }
  }, [notification]);

  useEffect(() => { fetchAll(); }, [fetchAll]);

  const linkedCount = useMemo(() => (rows || []).filter((r) => !!r?.google_id).length, [rows]);
  const unlinkedCount = useMemo(() => (rows || []).filter((r) => !r?.google_id).length, [rows]);

  return (
    <div className="page">
      <div style={{ marginBottom: 10, fontSize: 12, color: '#888' }}>
        Liés Google: <strong>{linkedCount}</strong> | Non liés: <strong>{unlinkedCount}</strong>
      </div>
      <div className="card" style={{ marginBottom: 14, padding: '10px 14px', fontSize: 12, color: '#666' }}>
        Pour lier un compte, vous devez être connecté avec ce compte admin. Le bouton "Lier" ouvre la page "Mon profil" où vous pouvez lancer la liaison Google.
      </div>
      <TableCard
        label="Comptes admin"
        loading={loading}
        rows={rows}
        toolbar={<button className="btn-outline" onClick={fetchAll}>Rafraîchir</button>}
      >
        {(pageRows) => (
        <table>
          <thead>
            <tr>
              <th>Email</th>
              <th>Nom</th>
              <th>Statut</th>
              <th>Google</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageRows.map((r) => {
              const linked = !!r?.google_id;
              const rejected = String(r?.status || '').toLowerCase() === 'rejected';
              return (
                <tr key={r.id}>
                  <td>{r.email}</td>
                  <td>{[r?.prenom, r?.nom].filter(Boolean).join(' ') || '-'}</td>
                  <td><span className="tag" style={{ background: `${statusColor(r?.status)}22`, color: statusColor(r?.status), border: `1px solid ${statusColor(r?.status)}44` }}>{statusLabel(r?.status)}</span></td>
                  <td><span className={`tag ${linked ? 'tag-green' : 'tag-grey'}`}>{linked ? 'Lié' : 'Non lié'}</span></td>
                  <td>
                    <div className="actions-cell">
                      <button
                        className={linked || rejected ? 'btn-outline' : 'btn-primary'}
                        disabled={linked || rejected}
                        onClick={() => navigate(`/admin/profile?link_email=${encodeURIComponent(String(r?.email || ''))}`)}
                        style={{ fontSize: 11, padding: '4px 10px' }}
                      >Lier</button>
                      <Popconfirm title="Délier Google ?" description="Cela retire le google_id du compte." okText="Délier" cancelText="Annuler" onConfirm={async () => { try { await unlinkGoogleFromUser(r.id); message.success('Compte Google délié.'); fetchAll(); } catch (e) { notification.error({ message: getApiErrorMessage(e, 'Déliaison Google échouée.'), placement: 'topRight' }); } }}>
                        <button className="btn-outline" disabled={!linked} style={{ fontSize: 11, padding: '4px 10px' }}>Délier Google</button>
                      </Popconfirm>
                      <Popconfirm title="Désactiver ce compte ?" description="Le compte passera en rejected." okText="Désactiver" cancelText="Annuler" onConfirm={async () => { try { await rejectAdminUser(r.id); message.success('Compte désactivé.'); fetchAll(); } catch (e) { notification.error({ message: getApiErrorMessage(e, 'Désactivation échouée.'), placement: 'topRight' }); } }}>
                        <button className="btn-outline danger" disabled={rejected} style={{ fontSize: 11, padding: '4px 10px' }}>Désactiver</button>
                      </Popconfirm>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        )}
      </TableCard>
    </div>
  );
}
